import clsx from 'clsx';
import { type ElementType, forwardRef, type ReactElement } from 'react';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import * as styles from './Link.sty.js';

const COMPONENT_NAME = 'Link';

/**
 * The props for the Link component.
 * @public
 */
export type LinkProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  WithChildren &
    AriaLabelingProps &
    StylingProps &
    DataTestId &
    MaskingProps &
    BehaviorTrackingProps
>;

/**
 * Use the `Link` component for navigational purposes within an app or view, such as navigating to another page in the same Dynatrace environment.
 * @public
 */
export const Link: <E extends ElementType = 'a'>(
  props: LinkProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    {
      children,
      as,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dt-component': dataDtComponent = COMPONENT_NAME,
      ...remainingProps
    }: LinkProps<E>,
    ref: typeof remainingProps.ref,
  ) => {
    const LinkTag = as || 'a';

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    return (
      <LinkTag
        ref={ref}
        className={clsx(styles.link, consumerClassName)}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={dataDtComponent}
      >
        {children}
      </LinkTag>
    );
  },
);

(Link as typeof Link & { displayName: string }).displayName = COMPONENT_NAME;
